if ( typeof StickyAddToCart !== 'function' ) {

	class StickyAddToCart extends HTMLElement {

		connectedCallback() {

			const id = this.dataset.sectionId;

			this._realForm = document.getElementById(`product-form-${id}`);
			this._realButton = this._realForm ? this._realForm.querySelector('[data-js-product-add-to-cart]') : null;
			this._realButtonText = this._realForm ? this._realForm.querySelector('[data-js-product-add-to-cart-text]') : null;
			this._priceEl = document.querySelector(`#product-price-${id} [data-js-product-price-original]`);
			this._variants = document.querySelector(`product-variants[data-id="${id}"]`);

			// Nothing to mirror or forward clicks to - stay inert rather than
			// show a bar that can't add anything to the cart.
			if ( ! this._realButton ) return;

			this._button = document.createElement('button');
			this._button.type = 'button';
			// Same button--solid look as the rest of the theme, plus the real
			// Add to cart button's own two classes (product__add-to-cart
			// product--add-to-cart-button) so it picks up that exact same
			// text-stays-visible / arrow-morphs-into-loader treatment
			// (section-main-product.css) for free - mirrors the real button's
			// current text (Add to cart/Select a size/Sold out) rather than a
			// fixed label. this._button itself never gets .working/.success
			// directly from a submit here (its click just forwards to the
			// real button below) - see the MutationObserver in sync() setup
			// further down, which copies those classes across from the real
			// button as they change.
			this._button.className = 'button button--solid sticky-add-to-cart__button product__add-to-cart product--add-to-cart-button';
			this._button.innerHTML = `
				<span class="add-to-cart__text"></span>
				<span class="add-to-cart__icon-slot" aria-hidden="true">
					<span class="add-to-cart__arrow"><svg class="icon" width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="-0.00012207" y="5.32214" width="14" height="2" fill="#111111"/><path d="M10.3635 0.322144L15.6363 6.32214L14.2726 7.82214L8.99989 1.82214L10.3635 0.322144Z" fill="#111111"/><path d="M8.99984 10.8222L14.2726 4.82217L15.6362 6.32211L10.3635 12.3222L8.99984 10.8222Z" fill="#111111"/></svg></span>
					<span class="lazy-preloader add-to-cart__preloader"><svg class="circular-loader" viewBox="25 25 50 50"><circle class="loader-path" cx="50" cy="50" r="20" fill="none" stroke-width="4"/></svg></span>
					<span class="add-to-cart__checkmark"><svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 6L5.5 10L14.5 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
				</span>
			`;
			this._buttonText = this._button.querySelector('.add-to-cart__text');
			this.append(this._button);

			// Mirrors .working/.success across from the real button rather
			// than duplicating component-product-form.js's fetch/timing logic -
			// this._button's own click just forwards to that real button (see
			// the click listener below), so the real button is always the one
			// actually driving the add-to-cart request and its class changes
			// are the single source of truth for both buttons' animation state.
			new MutationObserver(() => {
				this._button.classList.toggle('working', this._realButton.classList.contains('working'));
				this._button.classList.toggle('success', this._realButton.classList.contains('success'));
			}).observe(this._realButton, { attributes: true, attributeFilter: ['class'] });

			// Size selector: a native <select> mirroring the real size radios
			// (if this product has a size option), so picking a size here drives
			// the exact same flow as the real picker - set the matching radio
			// and dispatch a bubbling 'change', which <product-variants> already
			// listens for (price/stock/URL updates, and this bar's own sync()
			// below via VARIANT_CHANGE). No variant-resolution logic duplicated.
			const sizeItems = this._variants ? this._variants.querySelectorAll('.product-variant__item--size') : [];
			if ( sizeItems.length ) {
				this._sizeContainer = sizeItems[0].closest('[data-js-product-variant-container]');
				const nameEl = sizeItems[0].closest('[data-js-product-variant]').querySelector('.product-variant__name');
				this._sizeSelect = document.createElement('select');
				this._sizeSelect.className = 'sticky-add-to-cart__size-select';
				this._sizeSelect.setAttribute('aria-label', nameEl ? nameEl.textContent.trim() : 'Size');
				// Nothing is checked among the real radios until the customer
				// actually picks a size (see sections/main-product.liquid,
				// search pdp_precolour_injected's neighbour comment on
				// data-variant-required) - a plain <select> with no option
				// marked selected still auto-displays its first option as
				// selected regardless, silently misrepresenting that as a
				// real choice. This placeholder is what actually holds that
				// slot instead, so the select's displayed/actual value both
				// start truly empty, and picking the size that happens to be
				// first in the list still fires a genuine 'change' (a
				// same-value re-select never does).
				const hasCheckedInput = [...sizeItems].some((item) => item.querySelector('input').checked);
				if ( ! hasCheckedInput ) {
					const placeholder = document.createElement('option');
					placeholder.value = '';
					placeholder.disabled = true;
					placeholder.selected = true;
					placeholder.textContent = ( typeof KROWN !== 'undefined' && KROWN.settings.locales.products_variant_required ) || 'Select a size';
					this._sizeSelect.append(placeholder);
				}
				sizeItems.forEach((item) => {
					const input = item.querySelector('input');
					const label = item.querySelector('label');
					const option = document.createElement('option');
					option.value = input.value;
					option.textContent = label.textContent.trim();
					option.disabled = input.disabled;
					option.selected = input.checked;
					this._sizeSelect.append(option);
				});
				this._sizeSelect.addEventListener('change', () => {
					const target = this._sizeContainer.querySelector(`input[value="${CSS.escape(this._sizeSelect.value)}"]`);
					if ( ! target ) return;
					target.checked = true;
					target.dispatchEvent(new Event('change', { bubbles: true }));
				});
				this.append(this._sizeSelect);
			}

			// Published so other fixed-position UI (WhatsApp widget, back-to-top
			// button) can move out of this bar's way via CSS instead of guessing
			// its height. Re-measured on resize since font-size/line-wrapping can
			// change the bar's height across breakpoints and orientation changes.
			this._updateHeightVar = () => {
				document.documentElement.style.setProperty('--sticky-add-to-cart-height', `${this.offsetHeight}px`);
			};
			this._updateHeightVar();
			window.addEventListener('resize', this._updateHeightVar);

			this._button.addEventListener('click', () => {
				if ( this._button.getAttribute('aria-disabled') == 'true' ) return;
				this._realButton.click();
			});

			this.sync();
			if ( this._variants ) this._variants.addEventListener('VARIANT_CHANGE', () => this.sync());

			// A plain position check rather than IntersectionObserver's
			// change-notification: only the real Add to cart button's current
			// position relative to the viewport matters here, checked directly
			// each time, with no dependency on the browser reliably telling us
			// when a transition happened.
			//
			// Shows this bar whenever that real button is NOT on screen - scrolled
			// off the top, OR still below the fold (so it's also shown on first
			// paint when the button starts out of view). Any part of the button
			// inside the viewport counts as on screen.
			this._onScroll = () => {
				if ( this._scrollTicking ) return;
				this._scrollTicking = true;
				requestAnimationFrame(() => {
					const rect = this._realButton.getBoundingClientRect();
					const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
					const offScreen = rect.bottom <= 0 || rect.top >= viewportHeight;
					this.classList.toggle('is-visible', offScreen);
					document.body.classList.toggle('has-sticky-add-to-cart', offScreen);
					this._scrollTicking = false;
				});
			};
			window.addEventListener('scroll', this._onScroll, { passive: true });
			window.addEventListener('resize', this._onScroll);
			// The button's position also moves without any scroll/resize (images
			// loading in above it, accordions, variant changes), so re-check on load
			// and whenever the page's own height changes.
			window.addEventListener('load', this._onScroll);
			if ( typeof ResizeObserver === 'function' ) {
				this._resizeObserver = new ResizeObserver(() => this._onScroll());
				this._resizeObserver.observe(document.documentElement);
			}
			this._onScroll();

		}

		disconnectedCallback() {
			if ( this._onScroll ) {
				window.removeEventListener('scroll', this._onScroll);
				window.removeEventListener('resize', this._onScroll);
				window.removeEventListener('load', this._onScroll);
			}
			if ( this._resizeObserver ) this._resizeObserver.disconnect();
			if ( this._updateHeightVar ) window.removeEventListener('resize', this._updateHeightVar);
			document.body.classList.remove('has-sticky-add-to-cart');
		}

		sync() {
			// Mirrors the real button's current text (Add to cart/Select a
			// size/Sold out) rather than a fixed label.
			if ( this._realButtonText ) this._buttonText.textContent = this._realButtonText.textContent;
			if ( this._priceEl ) {
				// innerHTML, not textContent: the real price markup includes an
				// inline currency SVG (see snippets that render `| money`), which
				// a text-only copy would silently drop.
				const existing = this.querySelector('.sticky-add-to-cart__price');
				if ( existing ) {
					existing.innerHTML = this._priceEl.innerHTML;
				} else {
					this._priceText = document.createElement('span');
					this._priceText.className = 'sticky-add-to-cart__price';
					this._priceText.innerHTML = this._priceEl.innerHTML;
					this.insertBefore(this._priceText, this._button);
				}
			}
			const disabled = this._realButton.classList.contains('disabled');
			this._button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
			this._button.classList.toggle('is-disabled', disabled);

			// Reflect the real picker's current selection/availability back onto
			// the select, in case the size changed via the real picker instead
			// of this bar (they share the same underlying radio inputs).
			if ( this._sizeSelect && this._sizeContainer ) {
				[...this._sizeSelect.options].forEach((option) => {
					const input = this._sizeContainer.querySelector(`input[value="${CSS.escape(option.value)}"]`);
					if ( ! input ) return;
					option.disabled = input.disabled;
					option.selected = input.checked;
				});
			}
		}

	}

	if ( typeof customElements.get('sticky-add-to-cart') == 'undefined' ) {
		customElements.define('sticky-add-to-cart', StickyAddToCart);
	}

}
